import { Clock } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { usd } from "@/components/dash/utils";

const pct = (v) => `${Math.round(v * 100)}%`;
const mmss = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

const LOOK = {
  "BUY UP": { cls: "bg-[#10B981] text-[#04130D]", line: "Buy UP now" },
  "BUY DOWN": { cls: "bg-[#EF4444] text-white", line: "Buy DOWN now" },
  SELL: { cls: "bg-[#F59E0B] text-[#1A1003]", line: "Sell your position now" },
  HOLD: { cls: "bg-[#22D3EE] text-[#03141A]", line: "Keep your position" },
  WAIT: { cls: "bg-[#1F2433] text-slate-100", line: "Don't bet right now" },
  "NO DATA": { cls: "bg-[#3A1D1D] text-red-100", line: "Prices can't be verified — don't bet" },
};

// Owner's real position in the current market (from the read-only account feed), if any.
const myPosition = (account, live) => {
  const p = (account?.positions || []).find((x) => x.market === live.market.slug);
  if (!p || !p.contracts) return null;
  const side = p.contracts > 0 ? "Up" : "Down";
  const n = Math.abs(p.contracts);
  const bid = live.quotes[side].bid;
  const value = n * bid;
  return { side, n, cost: p.cost || 0, value, pct: p.cost ? (value / p.cost - 1) * 100 : 0 };
};

export const decide = (live, account, rules, remaining) => {
  if (!live || live.stale || live.status === "DATA_UNAVAILABLE") return { action: "NO DATA", why: "Live prices from Polymarket US couldn't be verified." };
  const pos = myPosition(account, live);
  if (pos) {
    if (pos.pct >= rules.take_profit_pct) return { action: "SELL", pos, why: `You're up ${pos.pct.toFixed(0)}% — that's your take-profit target. Lock it in.` };
    if (pos.pct <= rules.stop_loss_pct) return { action: "SELL", pos, why: `You're down ${Math.abs(pos.pct).toFixed(0)}% — that's your stop-loss. Cut the loss.` };
    if (remaining <= rules.exit_before_close_sec) return { action: "SELL", pos, why: "Under a minute left — sell now or ride it to the result." };
    return { action: "HOLD", pos, why: `You're ${pos.pct >= 0 ? "up" : "down"} ${Math.abs(pos.pct).toFixed(0)}%. No sell rule hit yet.` };
  }
  const f = live.result?.signal_filter || {};
  if (f.signal === "UP") return { action: "BUY UP", why: "All 6 safety checks passed and the signal has a proven track record." };
  if (f.signal === "DOWN") return { action: "BUY DOWN", why: "All 6 safety checks passed and the signal has a proven track record." };
  const notProven = (f.criteria || []).some((c) => c.name.startsWith("Validated") && !c.met);
  if (f.timeframes === "Conflicting") return { action: "WAIT", why: "Bitcoin is moving both ways (last 1, 5 and 15 minutes disagree)." };
  if (notProven) return { action: "WAIT", why: "The setup hasn't proven it wins often enough yet, so no bet." };
  return { action: "WAIT", why: "The signals don't line up. Waiting is the safest move." };
};

export const SimpleView = ({ live, account, rules, now }) => {
  if (!live || !rules) return <section className="card p-6"><Skeleton className="h-64 bg-[#111A2C]" /></section>;
  const remaining = Math.max(0, Math.round(live.market.end_ts - now / 1000));
  const d = decide(live, account, rules, remaining);
  const look = LOOK[d.action];
  const diff = live.btc.price - live.btc.start;
  const up = live.result?.market_implied?.up ?? live.market_up;
  return (
    <section className="card overflow-hidden fade-up" data-testid="simple-view">
      <div className={`p-6 sm:p-8 ${look.cls}`} data-testid="simple-action-box">
        <div className="text-[11px] uppercase tracking-widest font-num opacity-80">What to do now</div>
        <div className="font-head font-extrabold text-5xl sm:text-6xl tracking-tight mt-1" data-testid="simple-action">{d.action}</div>
        <div className="text-base font-semibold mt-1">{look.line}</div>
        <p className="text-sm mt-2 opacity-90 max-w-xl" data-testid="simple-why">{d.why}</p>
        {d.pos && <p className="text-xs mt-2 font-num opacity-90" data-testid="simple-position">Your position: {d.pos.n} {d.pos.side} · cost {usd(d.pos.cost)} · worth ≈ {usd(d.pos.value)}</p>}
      </div>
      <div className="grid grid-cols-3 divide-x divide-[#1A2338] text-center">
        <div className="p-4">
          <div className="text-[10px] uppercase tracking-widest font-num text-slate-400 flex items-center justify-center gap-1"><Clock className="w-3 h-3" />Time left</div>
          <div className="font-num font-bold text-xl mt-1" data-testid="simple-time-left">{mmss(remaining)}</div>
        </div>
        <div className="p-4">
          <div className="text-[10px] uppercase tracking-widest font-num text-slate-400">BTC vs start</div>
          <div className={`font-num font-bold text-xl mt-1 ${diff >= 0 ? "text-[#34D399]" : "text-[#F87171]"}`} data-testid="simple-btc-diff">{diff >= 0 ? "▲" : "▼"} {usd(Math.abs(diff))}</div>
        </div>
        <div className="p-4">
          <div className="text-[10px] uppercase tracking-widest font-num text-slate-400">Odds</div>
          <div className="font-num font-bold text-sm mt-1 leading-tight" data-testid="simple-odds"><div className="text-[#34D399]">Up {pct(up)}</div><div className="text-[#F87171]">Down {pct(1 - up)}</div></div>
        </div>
      </div>
      <div className="px-5 pb-5 text-[11px] text-slate-500">
        {live.market.question} · <a className="text-[#22D3EE]" href={live.market.url} target="_blank" rel="noreferrer" data-testid="simple-market-link">open on Polymarket US</a> · Not a guarantee.
      </div>
    </section>
  );
};

export const HowToCard = ({ rules }) => (
  <section className="card p-5 sm:p-6" data-testid="howto-card">
    <h2 className="font-head font-bold text-lg">How to use it (30 seconds)</h2>
    <div className="grid sm:grid-cols-3 gap-4 mt-3 text-sm">
      <div className="rounded-xl bg-[#10B981]/10 border border-[#10B981]/30 p-4" data-testid="howto-buy">
        <div className="font-bold text-[#34D399]">When to BUY</div>
        <p className="text-slate-300 mt-1">Only when the box says <b>BUY UP</b> or <b>BUY DOWN</b>. Buy that side on Polymarket US. Use a small amount (about 5% of your money).</p>
      </div>
      <div className="rounded-xl bg-slate-500/10 border border-slate-500/30 p-4" data-testid="howto-wait">
        <div className="font-bold text-slate-200">When NOT to buy</div>
        <p className="text-slate-300 mt-1">Whenever it says <b>WAIT</b> or <b>NO DATA</b>. Most of the time that's the right call — no bet is better than a bad bet.</p>
      </div>
      <div className="rounded-xl bg-[#F59E0B]/10 border border-[#F59E0B]/30 p-4" data-testid="howto-sell">
        <div className="font-bold text-amber-300">When to SELL</div>
        <p className="text-slate-300 mt-1">Up {rules?.take_profit_pct ?? 20}% → sell (take profit). Down {Math.abs(rules?.stop_loss_pct ?? 15)}% → sell (cut loss). Under {rules?.exit_before_close_sec ?? 60}s left → sell or let it settle.</p>
      </div>
    </div>
  </section>
);
